// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import type { Facility, Project, WorkOrderPaginated } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { BendCreatorForm } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendCreatorForm';
import { BendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { BendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import type { BendFormBendData } from 'modules/Shop/EvolveFabrication/BendElementDetails/BendElementDetailsForm';
import { CreateBendElementDetailsModal } from 'modules/Shop/EvolveFabrication/BendElementDetails/CreateBendElementDetailsModal';

type Props = {
  /** If provided, will control the Modal directly, hiding the "Bend creator" button */
  opened?: boolean;
  onClose?: () => void;
  onBendsCreated: () => void;
  selectedFacilities?: Facility[];
} & RequireAtLeastOne<{
  workOrder?: WorkOrderPaginated;
  selectedProjects?: Project[];
}>;

export const BendCreatorModal = ({ opened: parentOpened, onClose: parentOnClose, onBendsCreated, ...props }: Props) => {
  const [pendingRequest, setPendingRequest] = useState<BendFormBendData | null>(null);
  const [opened, setOpened] = useUncontrolled({
    value: parentOpened,
    onChange: parentOnClose,
    defaultValue: false,
  });

  const close = useCallback(() => setOpened(false), [setOpened]);

  return (
    <>
      {isNil(parentOpened) && (
        <Button leftSection={<EvolveIcon icon="BendCreator" />} onClick={() => setOpened(true)} variant="gradient">
          Bend creator
        </Button>
      )}

      <WrappedModal
        title="Bend creator"
        opened={opened}
        onClose={close}
        hideModalButtons
        bodyProps={{ p: 0, display: 'flex', flex: 1, style: { flexDirection: 'column' } }}
        fullScreen
      >
        <BendCreatorFormContext>
          <BendCreatorValidationContext>
            <BendCreatorForm
              onClose={close}
              onSubmit={({ benderProfile, ...rest }) => {
                setPendingRequest({ benderProfileId: benderProfile.machineProfileId, ...rest });
              }}
            />
          </BendCreatorValidationContext>
        </BendCreatorFormContext>
      </WrappedModal>
      <CreateBendElementDetailsModal
        bendData={pendingRequest}
        onBendsCreated={() => {
          onBendsCreated();
          close();
        }}
        onClose={() => setPendingRequest(null)}
        {...props}
      />
    </>
  );
};
